/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Skeleton, Typography, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { getSearchFilterOptionItemTranslations } from 'bundles/search-common/utils/getFilterItemsTranslations';
import { formatNumberForLocale } from 'bundles/search-common/utils/utils';
import { useSeoPageData } from 'bundles/seo/contexts/SeoPageDataContext';

import _t from 'i18n!nls/search-common';

type Props = {
  searchQuery?: string;
  numberOfHits?: number;
  shouldHide?: boolean;
  activeFilters?: string[];
  isLoading?: boolean;
  activeFilterCount?: number;
};

const styles = {
  header: (theme: Theme) => css`
    min-height: 36px;
    margin-bottom: 24px;

    @media (max-width: ${theme.breakpoints.values.md - 1}px) {
      min-height: 32px;
    }
  `,
  placeholderWrapper: css`
    padding-bottom: 4px;
    display: flex;
    flex-direction: column;

    .results-text-placeholder {
      position: relative;
      display: block;
      top: -2px;
      margin-bottom: 10px !important;
    }

    .filter-placeholder {
      padding-bottom: 4px;
      margin-top: 6px;
      display: flex;
      justify-content: left;

      .chip-placeholder {
        margin-right: 16px;
      }
    }
  `,
};

export const SearchResultsHeader: React.FC<Props> = ({
  searchQuery,
  numberOfHits,
  shouldHide,
  activeFilters,
  isLoading,
  activeFilterCount,
}) => {
  const theme = useTheme();
  const { eqpData } = useSeoPageData();
  const seoHeader = eqpData?.headers?.searchResultsHeader?.label;
  if (shouldHide) {
    return null;
  }
  if (isLoading) {
    return (
      <div css={styles.placeholderWrapper}>
        <Skeleton className="results-text-placeholder" width="17em" height="46px" />

        {Boolean(activeFilterCount) && (
          <div className="filter-placeholder">
            {[
              Array.from({ length: Math.min(activeFilterCount ?? 0, 5) }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Skeleton className="chip-placeholder" width="8em" height="46px" key={i} />
              )),
            ]}
          </div>
        )}
      </div>
    );
  }

  if (isLoading === false && numberOfHits === undefined) {
    return (
      <div className="rc-SearchResultsHeader">
        <Typography variant="h1semibold" css={styles.header(theme)}>
          <div data-e2e="NumberOfResultsSection">
            <FormattedMessage
              message={searchQuery ? _t('No results found for "{query}"') : _t('No results found for your search')}
              query={searchQuery}
            />
          </div>
        </Typography>
      </div>
    );
  }

  const getSearchResultText = () => {
    const hasActiveFilters = (activeFilters?.length ?? 0) > 0;

    if (numberOfHits !== 0) {
      if (seoHeader) {
        return <FormattedMessage message={seoHeader} />;
      }
      if (searchQuery && hasActiveFilters) {
        const translatedFilters = activeFilters
          ?.map((filter) => {
            const [category, label] = filter.split(':');
            const optionItemTranslations = getSearchFilterOptionItemTranslations()[category];
            const translatedItem = optionItemTranslations?.[label] || label || category;
            return typeof translatedItem !== 'string' ? translatedItem?.title : translatedItem;
          })
          .join(', ');
        return (
          <FormattedMessage
            message={_t(
              '{numberOfHits} {numberOfHits, plural, =1 { result} other { results}} for "{query}" filtered by {translatedFilters}'
            )}
            numberOfHits={formatNumberForLocale(numberOfHits ?? 0)}
            query={searchQuery}
            translatedFilters={translatedFilters}
          />
        );
      } else if (searchQuery) {
        return (
          <FormattedMessage
            message={_t('{numberOfHits} {numberOfHits, plural, =1 { result} other { results}} for "{query}"')}
            numberOfHits={formatNumberForLocale(numberOfHits ?? 0)}
            query={searchQuery}
          />
        );
      } else {
        return (
          <FormattedMessage
            message={_t('{numberOfHits} {numberOfHits, plural, =1 { result} other { results}}')}
            numberOfHits={formatNumberForLocale(numberOfHits ?? 0)}
          />
        );
      }
    } else {
      return (
        <FormattedMessage
          message={searchQuery ? _t('No results found for "{query}"') : _t('No results found for your search')}
          query={searchQuery}
        />
      );
    }
  };

  return (
    <div className="rc-SearchResultsHeader">
      <Typography variant="h1semibold" component="h2" role="heading" css={styles.header(theme)}>
        <div data-e2e="NumberOfResultsSection">{getSearchResultText()}</div>
      </Typography>
    </div>
  );
};

export default memo(SearchResultsHeader);
